// app/javascript/pages/Student/Tests/Index.tsx
import { useState, useMemo } from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  ArrowLeft, Coins, FileText, Search, CheckCircle2,
  X, RotateCcw, Flame, Crown, BookOpen, Target,
  Award, Filter, ArrowUpDown
} from 'lucide-react'

import { TestCard, type TestItem } from '@/components/student/TestCard'
import { SyllabusTreeModal } from '@/components/student/SyllabusTreeModal'

interface Props {
  exam?: { id: number; name: string; code: string } | null
  tests: TestItem[]
  credit_balance: number
}

type FilterTab = 'all' | 'new_hot' | 'premium' | 'unlocked' | 'attempted'
type SortOption = 'default' | 'questions_desc' | 'duration_asc' | 'duration_desc' | 'marks_desc'

export default function StudentTestsIndex({ exam, tests = [], credit_balance }: Props) {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [selectedExam, setSelectedExam] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortOption>('default')
  const [selectedSyllabusTest, setSelectedSyllabusTest] = useState<TestItem | null>(null)
  const [syllabusModalOpen, setSyllabusModalOpen] = useState(false)

  const handleOpenSyllabus = (test: TestItem) => {
    setSelectedSyllabusTest(test)
    setSyllabusModalOpen(true)
  }

  // Collect available exams and types for quick filter pills
  const availableExams = useMemo(() => {
    const names = Array.from(new Set(tests.map(t => t.exam_name).filter(Boolean) as string[]))
    return names.sort()
  }, [tests])

  const hasMultipleExams = availableExams.length > 1 && !exam

  // Categorized test collections
  const attemptedTests = useMemo(() => tests.filter((t) => t.attempt_data?.has_attempted), [tests])
  const unattemptedTests = useMemo(() => tests.filter((t) => !t.attempt_data?.has_attempted), [tests])
  const premiumTests = useMemo(() => tests.filter((t) => !t.has_access && !t.is_free), [tests])
  const unlockedUnattemptedTests = useMemo(() => unattemptedTests.filter((t) => t.has_access || t.is_free), [unattemptedTests])

  // Non-duplicating Hot & New selection (top 3 unattempted tests)
  const hotAndNewTests = useMemo(() => {
    return unlockedUnattemptedTests.slice(0, Math.min(3, unlockedUnattemptedTests.length))
  }, [unlockedUnattemptedTests])

  const hotIds = useMemo(() => new Set(hotAndNewTests.map(t => t.id)), [hotAndNewTests])

  // Remaining unlocked and premium tests without repeating items in Hot & New
  const remainingUnlocked = useMemo(() => {
    return unlockedUnattemptedTests.filter(t => !hotIds.has(t.id))
  }, [unlockedUnattemptedTests, hotIds])

  const remainingPremium = useMemo(() => {
    return premiumTests.filter(t => !hotIds.has(t.id))
  }, [premiumTests, hotIds])

  // Quick stats
  const unlockedCount = useMemo(() => tests.filter((t) => t.has_access || t.is_free).length, [tests])
  const attemptedCount = attemptedTests.length
  const premiumCount = premiumTests.length
  const completionPercentage = tests.length > 0 ? Math.round((attemptedCount / tests.length) * 100) : 0

  // Filtered test items for specific tab, type, exam, sort or search
  const filteredTests = useMemo(() => {
    let result = tests.filter((test) => {
      // Search matching title, description, exam, or type
      if (search.trim()) {
        const query = search.toLowerCase()
        const matches =
          test.title.toLowerCase().includes(query) ||
          (test.description && test.description.toLowerCase().includes(query)) ||
          (test.exam_name && test.exam_name.toLowerCase().includes(query)) ||
          test.test_type.toLowerCase().includes(query)

        if (!matches) return false
      }

      // Primary tab filter
      if (activeTab === 'new_hot') {
        if (test.attempt_data?.has_attempted) return false
      } else if (activeTab === 'premium') {
        if (test.has_access || test.is_free) return false
      } else if (activeTab === 'unlocked') {
        if (!test.has_access && !test.is_free) return false
        if (test.attempt_data?.has_attempted) return false
      } else if (activeTab === 'attempted') {
        if (!test.attempt_data?.has_attempted) return false
      }

      // Secondary Type filter
      if (selectedType !== 'all') {
        const tKey = test.test_type.toLowerCase()
        if (selectedType === 'sectional') {
          if (!tKey.includes('sectional')) return false
        } else if (selectedType === 'full_mock') {
          if (tKey !== 'full_mock') return false
        } else if (selectedType === 'chapter_test') {
          if (!tKey.includes('chapter')) return false
        } else if (selectedType === 'speed_drill') {
          if (!tKey.includes('speed')) return false
        } else if (selectedType === 'practice') {
          if (!tKey.includes('practice')) return false
        } else if (tKey !== selectedType.toLowerCase()) {
          return false
        }
      }

      // Secondary Exam filter
      if (selectedExam !== 'all' && test.exam_name) {
        if (test.exam_name !== selectedExam) return false
      }

      return true
    })

    // Sorting
    if (sortBy === 'questions_desc') {
      result = [...result].sort((a, b) => (b.question_count || 0) - (a.question_count || 0))
    } else if (sortBy === 'duration_asc') {
      result = [...result].sort((a, b) => (a.duration_minutes || 0) - (b.duration_minutes || 0))
    } else if (sortBy === 'duration_desc') {
      result = [...result].sort((a, b) => (b.duration_minutes || 0) - (a.duration_minutes || 0))
    } else if (sortBy === 'marks_desc') {
      result = [...result].sort((a, b) => (b.total_marks || 0) - (a.total_marks || 0))
    }

    return result
  }, [tests, search, activeTab, selectedType, selectedExam, sortBy])

  const hasActiveFilters = Boolean(
    search.trim() ||
    activeTab !== 'all' ||
    selectedType !== 'all' ||
    selectedExam !== 'all' ||
    sortBy !== 'default'
  )

  const isSectionedView = activeTab === 'all' && !hasActiveFilters

  const handleResetFilters = () => {
    setSearch('')
    setActiveTab('all')
    setSelectedType('all')
    setSelectedExam('all')
    setSortBy('default')
  }

  return (
    <StudentShell>
      {/* ─── MOBILE VIEW ONLY (< lg) ─── */}
      <div className="block lg:hidden space-y-3 pb-6">
        {/* Mobile Header Card */}
        <div className="bg-card border border-border/80 rounded-2xl p-3 shadow-glass-xs space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {exam && (
                  <Link href="/student/exams" className="w-6 h-6 flex items-center justify-center rounded-lg border border-border hover:bg-muted/50 transition-colors shrink-0 mr-0.5">
                    <ArrowLeft className="h-3.5 w-3.5 text-muted-foreground" />
                  </Link>
                )}
                <h1 className="text-base font-extrabold text-foreground tracking-tight truncate">
                  {exam ? exam.name : 'Practice Tests'}
                </h1>
              </div>
              <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                {exam ? `${exam.code} Mock Tests` : 'All available mock papers'}
              </p>
            </div>

            {/* Credit Balance Pill */}
            <div className="flex items-center gap-1 bg-tertiary/15 border border-tertiary/30 px-2 py-0.5 rounded-full text-[11px] font-extrabold text-tertiary shrink-0 shadow-2xs">
              <Coins className="h-3 w-3" />
              <span>{credit_balance}</span>
            </div>
          </div>

          {/* Integrated Compact Progress Bar */}
          {tests.length > 0 && (
            <div className="pt-2 border-t border-border/40 space-y-1">
              <div className="flex items-center justify-between text-[10px] font-bold">
                <span className="text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  <span>Preparation</span>
                </span>
                <span className="text-foreground">{attemptedCount} / {tests.length} ({completionPercentage}%)</span>
              </div>
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, completionPercentage)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Search Bar (Mobile) */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tests by title or exam..."
            className="pl-8.5 pr-8 rounded-xl border-border bg-card h-9 text-xs"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter Tab Pills (Mobile - Scrollable) */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
          {(['all', 'new_hot', 'premium', 'unlocked', 'attempted'] as const).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-none px-2.5 py-1 rounded-xl text-xs font-semibold border transition-all whitespace-nowrap cursor-pointer ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground border-primary font-bold shadow-2xs'
                  : 'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
              }`}
            >
              {tab === 'all' ? `All (${tests.length})`
               : tab === 'new_hot' ? `🔥 Hot & New`
               : tab === 'premium' ? `👑 Premium (${premiumCount})`
               : tab === 'unlocked' ? `✨ Free (${unlockedCount})`
               : `📝 Attempted (${attemptedCount})`}
            </button>
          ))}
        </div>

        {/* Secondary Filter & Sort Toolbar for Mobile */}
        <div className="flex items-center justify-between gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none]">
          {/* Mobile Type Chips */}
          <div className="flex items-center gap-1 overflow-x-auto shrink-0 [scrollbar-width:none]">
            {[
              { key: 'all', label: 'All' },
              { key: 'full_mock', label: 'Full Mocks' },
              { key: 'sectional', label: 'Sectionals' },
              { key: 'chapter_test', label: 'Chapter' },
              { key: 'practice', label: 'Practice' },
            ].map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setSelectedType(t.key)}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap cursor-pointer ${
                  selectedType === t.key
                    ? 'bg-primary/20 text-primary border border-primary/40'
                    : 'bg-muted/50 text-muted-foreground border border-transparent'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Mobile Sort Dropdown */}
          <div className="shrink-0 ml-auto">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortOption)}
              className="h-7 text-[11px] font-bold bg-card rounded-lg border border-border/80 pl-1.5 pr-6 py-0 text-foreground focus:outline-none leading-none cursor-pointer"
            >
              <option value="default">Sort: Recommended</option>
              <option value="questions_desc">Sort: Most Questions</option>
              <option value="duration_asc">Sort: Shortest Time</option>
              <option value="duration_desc">Sort: Longest Time</option>
              <option value="marks_desc">Sort: Highest Marks</option>
            </select>
          </div>
        </div>

        {/* Active Filter Pill with Reset on Mobile */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between text-[11px] px-1 text-muted-foreground">
            <span>{filteredTests.length} tests found</span>
            <button
              type="button"
              onClick={handleResetFilters}
              className="text-primary font-bold hover:underline inline-flex items-center gap-0.5"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Reset</span>
            </button>
          </div>
        )}

        {/* Tests List (Mobile) */}
        <div className="space-y-3">
          {filteredTests.length === 0 ? (
            <Card className="p-6 text-center border-border/80 bg-card rounded-2xl space-y-2.5">
              <p className="text-xs font-bold text-foreground">No tests match your filter</p>
              <Button size="sm" variant="outline" onClick={handleResetFilters} className="text-xs rounded-xl h-8">
                Reset Filters
              </Button>
            </Card>
          ) : (
            filteredTests.map(test => (
              <TestCard key={test.id} test={test} onViewSyllabus={handleOpenSyllabus} />
            ))
          )}
        </div>
      </div>

      {/* ─── DESKTOP VIEW ONLY (>= lg) ─── */}
      <div className="hidden lg:block space-y-4 max-w-7xl mx-auto pb-12">
        {/* Navigation Breadcrumb (When viewing a specific exam) */}
        {exam && (
          <div>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="rounded-xl border-border/80 bg-card text-foreground hover:bg-primary/10 hover:border-primary/50 transition-all shadow-glass-xs h-8 px-3 text-xs"
            >
              <Link href={`/student/exams/${exam.id}`}>
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                <span>Back to {exam.name}</span>
              </Link>
            </Button>
          </div>
        )}

        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/80 bg-card p-4 sm:p-5 shadow-glass-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1.5 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1 shadow-2xs">
                  <Target className="h-3 w-3 text-primary" />
                  <span>{exam ? exam.name : 'Target Practice'}</span>
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full border border-border/60">
                  Adaptive Mock Portal
                </span>
              </div>

              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                  {exam ? `${exam.name} Test Series` : 'Exam Mock Tests & Series'}
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Practice timed full-length mocks, sectional tests, and speed drills to learn and master core concepts.
                </p>
              </div>

              {/* Prep Progress Bar */}
              {tests.length > 0 && (
                <div className="pt-0.5 max-w-md space-y-1">
                  <div className="flex items-center justify-between text-[11px] font-semibold">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-primary" />
                      <span>Preparation Progress</span>
                    </span>
                    <span className="text-foreground font-bold">
                      {attemptedCount} / {tests.length} Completed ({completionPercentage}%)
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-muted/70 rounded-full overflow-hidden border border-border/40">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, completionPercentage)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Quick Metrics Strip */}
            <div className="grid grid-cols-3 gap-2 shrink-0 lg:max-w-xs w-full">
              <div className="flex flex-col justify-center p-2.5 rounded-xl bg-primary/10 border border-primary/25 transition-all">
                <div className="flex items-center gap-1">
                  <FileText className="h-3 w-3 text-primary shrink-0" />
                  <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Mocks</span>
                </div>
                <span className="text-lg sm:text-xl font-extrabold text-primary mt-0.5">{tests.length}</span>
              </div>

              <div className="flex flex-col justify-center p-2.5 rounded-xl bg-secondary/10 border border-secondary/25 transition-all">
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-secondary shrink-0" />
                  <span className="text-[10px] font-bold text-secondary uppercase tracking-wider">Attempted</span>
                </div>
                <span className="text-lg sm:text-xl font-extrabold text-secondary mt-0.5">{attemptedCount}</span>
              </div>

              <div className="flex flex-col justify-center p-2.5 rounded-xl bg-tertiary/10 border border-tertiary/25 transition-all">
                <div className="flex items-center gap-1">
                  <Coins className="h-3 w-3 text-tertiary shrink-0" />
                  <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider">Credits</span>
                </div>
                <span className="text-lg sm:text-xl font-extrabold text-tertiary mt-0.5">{credit_balance}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── PRIMARY FILTER & SEARCH TOOLBAR ─── */}
        <div className="bg-card border border-border/80 rounded-2xl p-2.5 sm:p-3 shadow-glass-xs space-y-2.5">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5">
            {/* Primary Status Tabs */}
            <div className="flex items-center p-0.5 bg-muted/40 rounded-xl border border-border/60 overflow-x-auto shrink-0 shadow-2xs gap-1">
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  activeTab === 'all'
                    ? 'bg-card text-foreground shadow-2xs border border-border/60'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span>All Mock Tests</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-extrabold ${activeTab === 'all' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {tests.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('new_hot')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  activeTab === 'new_hot'
                    ? 'bg-card text-foreground shadow-2xs border border-border/60'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Flame className="h-3.5 w-3.5 text-destructive fill-destructive" />
                <span>Hot & New</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-extrabold ${activeTab === 'new_hot' ? 'bg-destructive/15 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                  {unattemptedTests.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('premium')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  activeTab === 'premium'
                    ? 'bg-card text-foreground shadow-2xs border border-border/60'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Crown className="h-3.5 w-3.5 text-tertiary" />
                <span>Premium</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-extrabold ${activeTab === 'premium' ? 'bg-tertiary/15 text-tertiary' : 'bg-muted text-muted-foreground'}`}>
                  {premiumCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('unlocked')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  activeTab === 'unlocked'
                    ? 'bg-card text-foreground shadow-2xs border border-border/60'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                <span>Free / Unlocked</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-extrabold ${activeTab === 'unlocked' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {unlockedCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('attempted')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  activeTab === 'attempted'
                    ? 'bg-card text-foreground shadow-2xs border border-border/60'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <RotateCcw className="h-3.5 w-3.5 text-secondary" />
                <span>Attempted</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-extrabold ${activeTab === 'attempted' ? 'bg-secondary/15 text-secondary' : 'bg-muted text-muted-foreground'}`}>
                  {attemptedCount}
                </span>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Search by test name, exam or type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8.5 pr-8 h-8 rounded-xl bg-background border-border/80 text-xs font-medium focus-visible:ring-primary"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  title="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Secondary Filter Row: Types, Exams, Sorting */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border/40 text-xs">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-bold text-muted-foreground uppercase flex items-center gap-1 mr-0.5">
                <Filter className="h-3 w-3" /> Type:
              </span>
              {[
                { key: 'all', label: 'All Types' },
                { key: 'full_mock', label: 'Full Mocks' },
                { key: 'sectional', label: 'Sectionals' },
                { key: 'chapter_test', label: 'Chapter Tests' },
                { key: 'practice', label: 'Practice Sets' },
              ].map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setSelectedType(t.key)}
                  className={`px-2 py-0.5 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                    selectedType === t.key
                      ? 'bg-primary text-primary-foreground font-bold shadow-2xs'
                      : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {t.label}
                </button>
              ))}

              {/* Optional Exam Selector */}
              {hasMultipleExams && (
                <div className="ml-2 flex items-center gap-1.5">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase">Exam:</span>
                  <select
                    value={selectedExam}
                    onChange={e => setSelectedExam(e.target.value)}
                    className="h-8 text-xs font-semibold bg-background rounded-lg border border-border/80 pl-2.5 pr-7 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer leading-normal min-w-[120px]"
                  >
                    <option value="all">All Exams</option>
                    {availableExams.map(ex => (
                      <option key={ex} value={ex}>{ex}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Sort Selector - Generous padding and min-width to avoid text clipping */}
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[11px] font-bold text-muted-foreground uppercase flex items-center gap-1 shrink-0">
                <ArrowUpDown className="h-3 w-3" /> Sort:
              </span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortOption)}
                className="h-8 text-xs font-semibold bg-background rounded-lg border border-border/80 pl-2.5 pr-7 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer leading-normal min-w-[135px]"
              >
                <option value="default">Recommended</option>
                <option value="questions_desc">Most Questions</option>
                <option value="duration_asc">Shortest Duration</option>
                <option value="duration_desc">Longest Duration</option>
                <option value="marks_desc">Highest Marks</option>
              </select>
            </div>
          </div>

          {/* Active Filter Indicator Bar */}
          {hasActiveFilters && (
            <div className="flex items-center justify-between pt-1.5 border-t border-border/40 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="font-semibold text-foreground">{filteredTests.length}</span> test{filteredTests.length === 1 ? '' : 's'} found
                {selectedType !== 'all' && (
                  <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/30">
                    Type: {selectedType.replace('_', ' ')}
                  </Badge>
                )}
                {selectedExam !== 'all' && (
                  <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/30">
                    Exam: {selectedExam}
                  </Badge>
                )}
                {search.trim() && (
                  <Badge variant="outline" className="text-[10px] font-bold bg-muted text-muted-foreground">
                    Keyword: "{search}"
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="h-6 text-xs font-bold text-primary hover:text-primary/80 hover:bg-primary/10 px-2 gap-1 rounded-lg"
              >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Filters</span>
              </Button>
            </div>
          )}
        </div>

        {/* ─── SECTIONED VIEW (Default "All" tab when no filters are active) ─── */}
        {isSectionedView ? (
          <div className="space-y-6">
            {/* 1. Hot & Featured Section (Top) */}
            {hotAndNewTests.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-destructive/15 border border-destructive/25 flex items-center justify-center text-destructive shrink-0 shadow-2xs">
                      <Flame className="h-4.5 w-4.5 fill-current" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h2 className="text-base sm:text-lg font-extrabold text-foreground">
                          Hot & Featured Tests
                        </h2>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-destructive/15 text-destructive border border-destructive/30">
                          Recommended
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Fresh mock papers to kickstart your preparation</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {hotAndNewTests.map((test, i) => (
                    <TestCard
                      key={test.id}
                      test={test}
                      isHot={i === 0}
                      isNew={i > 0}
                      onViewSyllabus={handleOpenSyllabus}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 2. Premium Pro Series Section (Without duplicating tests in Hot & New!) */}
            {remainingPremium.length > 0 && (
              <section className="space-y-4">
                <div className="rounded-2xl border border-tertiary/30 bg-card p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-tertiary/20 border border-tertiary/40 flex items-center justify-center text-tertiary shrink-0 shadow-2xs">
                      <Crown className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-base sm:text-lg font-extrabold text-foreground">
                          Premium Pro Series
                        </h2>
                        <Badge className="text-[10px] bg-tertiary/25 text-tertiary border border-tertiary/40 uppercase font-black px-2 py-0.2">
                          High Yield
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Full-length practice tests with comprehensive performance analysis & verified solutions
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs font-semibold text-tertiary shrink-0 sm:ml-auto">
                    <Award className="h-4 w-4" />
                    <span>All-India Rank Included</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {remainingPremium.map((test) => (
                    <TestCard
                      key={test.id}
                      test={test}
                      onViewSyllabus={handleOpenSyllabus}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 3. Free Practice Deck */}
            {remainingUnlocked.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center text-primary">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-extrabold text-foreground">
                      Free Practice Deck
                    </h2>
                    <p className="text-xs text-muted-foreground">Accessible test papers ready for immediate attempt</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {remainingUnlocked.map((test) => (
                    <TestCard
                      key={test.id}
                      test={test}
                      onViewSyllabus={handleOpenSyllabus}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 4. Completed Attempts Section (Bottom) */}
            {attemptedTests.length > 0 && (
              <section className="space-y-4 pt-4 border-t border-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-xl bg-secondary/15 border border-secondary/25 flex items-center justify-center text-secondary">
                      <RotateCcw className="h-4 w-4" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-extrabold text-foreground flex items-center gap-2">
                        <span>Your Completed Attempts</span>
                        <Badge variant="outline" className="text-[10px] bg-secondary/15 text-secondary border-secondary/30 font-bold px-2 py-0">
                          {attemptedTests.length} Tests
                        </Badge>
                      </h2>
                      <p className="text-xs text-muted-foreground">Review your past scores, deep solutions, or retake to boost accuracy</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {attemptedTests.map((test) => (
                    <TestCard
                      key={test.id}
                      test={test}
                      onViewSyllabus={handleOpenSyllabus}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          /* ─── FILTERED TAB / SEARCH VIEW ─── */
          <div>
            {filteredTests.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {filteredTests.map((test) => (
                    <TestCard
                      key={test.id}
                      test={test}
                      onViewSyllabus={handleOpenSyllabus}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Empty State */
              <Card className="p-10 sm:p-14 text-center border-border/80 bg-card rounded-3xl space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto border border-border/60 shadow-xs">
                  <FileText className="h-7 w-7" />
                </div>
                <div className="space-y-1.5 max-w-sm mx-auto">
                  <h3 className="text-base sm:text-lg font-bold text-foreground">
                    {search ? 'No Matching Tests Found' : 'No Tests Found for this Filter'}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {search
                      ? `We couldn't find any mock tests matching "${search}". Try adjusting your keywords or clearing filters.`
                      : 'There are currently no mock tests matching the selected criteria.'}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleResetFilters}
                  className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold px-4"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Reset All Filters
                </Button>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Syllabus Tree Modal */}
      <SyllabusTreeModal
        test={selectedSyllabusTest}
        open={syllabusModalOpen}
        onOpenChange={setSyllabusModalOpen}
      />
    </StudentShell>
  )
}
